import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Play, Download, FileText, Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TEMPLATE_ROLES = ["hr", "super_admin"];

export default function Payroll() {
  const { user } = useAuth();
  const canCustomize = TEMPLATE_ROLES.includes(user?.role);
  const [stats, setStats] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [template, setTemplate] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => { api.get("/reports/summary").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    if (canCustomize) {
      api.get("/payslip-template").then((r) => setTemplate(r.data)).catch(() => {});
    }
  }, [canCustomize]);

  const generate = useCallback(async (row) => {
    try {
      const { data } = await api.post(`/payroll/${row.id}/payslip`);
      toast.success("Payslip generated");
      const token = localStorage.getItem("hr_token");
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}/download?auth=${token}`, "_blank");
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }, []);

  const bulkGenerate = async () => {
    setBulkRunning(true);
    try {
      const { data } = await api.post("/payslip-bulk-generate", {});
      toast.success(`${data.generated}/${data.total} payslips generated`);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const saveTemplate = async () => {
    setSavingTpl(true);
    try {
      await api.put("/payslip-template", template);
      toast.success("Template saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingTpl(false);
    }
  };

  const downloadPayslip = useCallback((row) => {
    if (!row.payslip_file_id) { toast.info("No payslip generated yet"); return; }
    const token = localStorage.getItem("hr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${row.payslip_file_id}/download?auth=${token}`, "_blank");
  }, []);

  const payrollColumns = useMemo(() => ([
    { key: "month", label: "Month" },
    { key: "employee_id", label: "ID", mono: true },
    { key: "employee_name", label: "Employee" },
    { key: "gross", label: "Gross", align: "right", render: (r) => `₹ ${(r.gross||0).toLocaleString("en-IN")}` },
    { key: "deductions", label: "Deductions", align: "right", render: (r) => `₹ ${(r.deductions||0).toLocaleString("en-IN")}` },
    { key: "net", label: "Net", align: "right", render: (r) => <span className="font-mono font-medium text-[#0F172A]">₹ {(r.net||0).toLocaleString("en-IN")}</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "Payslip", render: (r) => (
      <div className="flex gap-1.5">
        {r.payslip_file_id ? (
          <Button data-testid={`download-payslip-${r.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={(e)=>{e.stopPropagation(); downloadPayslip(r);}}>
            <Download className="h-3 w-3 mr-1" /> View
          </Button>
        ) : null}
        <Button data-testid={`generate-payslip-${r.id}`} size="sm" className="h-7 text-xs bg-[#1E88E5] hover:bg-[#1976d2]" onClick={(e)=>{e.stopPropagation(); generate(r);}}>
          <FileText className="h-3 w-3 mr-1" /> {r.payslip_file_id ? "Regen" : "Generate"}
        </Button>
      </div>
    )},
  ]), [downloadPayslip, generate]);

  const cards = [
    { label: "This month's payout", value: stats ? `₹ ${(stats.payroll_total || 0).toLocaleString("en-IN")}` : "—" },
    { label: "Payslips generated", value: "—", live: "payslipsCount" },
    { label: "Pending processing", value: "—", live: "pending" },
  ];

  return (
    <div data-testid="payroll-page">
      <PageHeader
        title="Payroll"
        subtitle="Monthly salary processing, payslip generation, and HR-customizable template."
        action={
          <Button
            data-testid="payroll-bulk"
            onClick={bulkGenerate}
            disabled={bulkRunning}
            className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]"
          >
            {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1.5" /> Generate all payslips</>}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <Card key={c.label} className="hr-card p-5">
            <div className="text-xs uppercase tracking-wider text-gray-500">{c.label}</div>
            <div className="hr-kpi mt-1.5">{c.value}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="run">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="run" data-testid="tab-run" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Monthly run</TabsTrigger>
          {canCustomize && (
            <TabsTrigger value="template" data-testid="tab-template" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Payslip template</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="run">
          <DataTable
            testId="payroll-table"
            endpoint="/payroll"
            refreshKey={refresh}
            columns={payrollColumns}
          />
        </TabsContent>

        {canCustomize && (
        <TabsContent value="template">
          <Card className="hr-card p-6 max-w-3xl" data-testid="template-form">
            <div className="flex items-center gap-2 mb-1">
              <Settings2 className="h-4 w-4 text-[#0F172A]" />
              <div className="font-display text-lg font-semibold text-[#0F172A]">Customize payslip</div>
            </div>
            <p className="text-xs text-gray-500 mb-4">These values appear on every payslip PDF and are applied to future generations.</p>
            {!template ? (
              <div className="text-sm text-gray-500">Loading template…</div>
            ) : (
              <div className="space-y-6">
                {/* Brand / Identity */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Branding</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Company logo">
                      <CompanyLogoUploader template={template} setTemplate={setTemplate} />
                    </Field>
                    <Field label="Header note">
                      <Input data-testid="tpl-header" value={template.header_note || ""} onChange={(e) => setTemplate({ ...template, header_note: e.target.value })} />
                    </Field>
                    <Field label="Accent color (HEX)">
                      <Input data-testid="tpl-accent" value={template.accent_color || ""} onChange={(e) => setTemplate({ ...template, accent_color: e.target.value })} placeholder="#F26B3A" />
                    </Field>
                  </div>
                </div>

                {/* Identity toggles */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Show on payslip</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { key: "show_company_logo", label: "Company logo" },
                      { key: "show_emp_id", label: "Employee ID" },
                      { key: "show_bank_ifsc", label: "Bank IFSC code" },
                      { key: "show_uan", label: "UAN number" },
                      { key: "show_pf_number", label: "PF number" },
                    ].map((t) => (
                      <label key={t.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`tpl-${t.key}`}
                          checked={!!template[t.key]}
                          onChange={(e) => setTemplate({ ...template, [t.key]: e.target.checked })}
                          className="h-4 w-4"
                        />
                        <span>{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Salary % split */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Salary breakdown (% of gross)</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Basic %">
                      <Input data-testid="tpl-basic" type="number" step="0.01" value={template.basic_pct || 0} onChange={(e) => setTemplate({ ...template, basic_pct: Number(e.target.value) })} />
                    </Field>
                    <Field label="HRA %">
                      <Input data-testid="tpl-hra" type="number" step="0.01" value={template.hra_pct || 0} onChange={(e) => setTemplate({ ...template, hra_pct: Number(e.target.value) })} />
                    </Field>
                    <Field label="Special %">
                      <Input data-testid="tpl-special" type="number" step="0.01" value={template.special_pct || 0} onChange={(e) => setTemplate({ ...template, special_pct: Number(e.target.value) })} />
                    </Field>
                    <Field label="PF %">
                      <Input data-testid="tpl-pf" type="number" step="0.01" value={template.pf_pct || 0} onChange={(e) => setTemplate({ ...template, pf_pct: Number(e.target.value) })} />
                    </Field>
                  </div>
                </div>

                {/* Additional earnings */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-3">Additional earnings &amp; deductions</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Variable Pay % of gross">
                      <Input data-testid="tpl-variable" type="number" step="0.01" min="0" max="1" value={template.variable_pay_pct || 0} onChange={(e) => setTemplate({ ...template, variable_pay_pct: Number(e.target.value) })} placeholder="0.05" />
                    </Field>
                    <Field label="Mobile / Internet allowance (INR)">
                      <Input data-testid="tpl-mobile" type="number" value={template.mobile_internet_allowance || 0} onChange={(e) => setTemplate({ ...template, mobile_internet_allowance: Number(e.target.value) })} placeholder="1500" />
                    </Field>
                    <Field label="Insurance deduction (INR)">
                      <Input data-testid="tpl-insurance" type="number" value={template.insurance_deduction || 0} onChange={(e) => setTemplate({ ...template, insurance_deduction: Number(e.target.value) })} placeholder="600" />
                    </Field>
                  </div>
                </div>

                <Field label="Footer note">
                  <Textarea data-testid="tpl-footer" rows={2} value={template.footer_note || ""} onChange={(e) => setTemplate({ ...template, footer_note: e.target.value })} />
                </Field>
              </div>
            )}
            <div className="mt-6 flex items-center gap-3">
              <Button data-testid="save-template" onClick={saveTemplate} disabled={savingTpl || !template} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
                {savingTpl ? "Saving…" : "Save template"}
              </Button>
              <span className="text-[11px] text-gray-500">Note: existing payslips keep their original look. New generations use this template.</span>
            </div>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gray-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CompanyLogoUploader({ template, setTemplate }) {
  const [uploading, setUploading] = useState(false);
  const fid = template.company_logo_file_id;

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo too large (max 5 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/files/upload?category=company_logo`, fd);
      setTemplate({ ...template, company_logo_file_id: data.id, company_logo_name: data.original_filename });
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const viewLogo = () => {
    const token = localStorage.getItem("hr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${token}`, "_blank");
  };

  return (
    <div className="flex items-center gap-3">
      {fid ? (
        <div className="flex items-center gap-2 text-sm flex-1">
          <FileText className="h-4 w-4 text-emerald-600" />
          <span className="flex-1 truncate text-gray-700">{template.company_logo_name || "Logo uploaded"}</span>
          <button type="button" onClick={viewLogo} className="text-[#1E88E5] hover:underline text-xs" data-testid="tpl-logo-view">View</button>
          <button type="button" onClick={() => setTemplate({ ...template, company_logo_file_id: "", company_logo_name: "" })} className="text-rose-600 hover:underline text-xs" data-testid="tpl-logo-remove">Remove</button>
        </div>
      ) : (
        <label className="block cursor-pointer flex-1">
          <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onPick} className="hidden" data-testid="tpl-logo-input" />
          <div className="border-2 border-dashed border-gray-200 rounded-md px-3 py-2 text-center hover:border-[#F26B3A] transition-colors text-xs text-gray-500">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" />Uploading…</> : "Click to upload logo (PNG, JPG, max 5 MB)"}
          </div>
        </label>
      )}
    </div>
  );
}
